import React, { useEffect, useState, useRef } from 'react';
import { useParams, useHistory } from '../../hooks/useRouterCompat';
import algoliasearch, { SearchClient } from 'algoliasearch/lite';
import {
  InstantSearch,
  Configure,
  useSearchBox,
  useInfiniteHits,
  useInstantSearch,
  Highlight
} from 'react-instantsearch-hooks-web';
import { Trans, t } from '@lingui/macro';
import { Search, X, MessagesSquare, ArrowDown, Check, Loader2, Menu as MenuIcon } from 'lucide-react';

import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { Util } from '../../services/Utils/Util';
import { UtilApp } from '../../services/Utils/UtilApp';
import { useAppUIContext } from '../../context/AppUIContext';
import { Workspace } from '../../models/Workspace';
import { Room } from '../../models/Room';

import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import { Separator } from '../../components/ui/separator';
import { Avatar, AvatarImage, AvatarFallback } from '../../components/ui/avatar';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { useToast } from '../../components/ui/use-toast';
import MessageFileItem from '../components/MessageFileItem';

// -- Custom Hooks / Components --

// Search Query Sync - Syncs external searchQuery to Algolia (like RoomSearchResults.tsx)
const SearchQuerySync: React.FC<{ query: string }> = ({ query }) => {
  const { refine } = useSearchBox();

  useEffect(() => {
    refine(query);
  }, [query, refine]);

  return null;
};

// Room Selector (Workspace Grouped)
interface RoomSelectorProps {
  workspaces: Workspace[];
  allRooms: Room[];
  selectedRoomIds: Set<string>;
  onToggleRoom: (roomId: string) => void;
  onToggleWorkspace: (rooms: Room[], select: boolean) => void;
}

function RoomSelector({ workspaces, allRooms, selectedRoomIds, onToggleRoom, onToggleWorkspace }: RoomSelectorProps) {
  const [workspaceRooms, setWorkspaceRooms] = useState(new Map<Workspace, Room[]>());

  useEffect(() => {
    let map = new Map<Workspace, Room[]>();
    workspaces.forEach(w => map.set(w, []));

    const standardRooms = allRooms.filter(r => !r.isOneToOne && r.visibility !== 'personal');

    standardRooms.forEach(r => {
      if (r.workspaceId) {
        const ws = workspaces.find(w => w.id === r.workspaceId);
        if (ws) {
          map.get(ws)?.push(r);
        }
      } else {
        const privateWs = new Workspace("private", t`Private`, My.user);
        if (!map.has(privateWs)) map.set(privateWs, []);
        map.get(privateWs)?.push(r);
      }
    });

    setWorkspaceRooms(map);

  }, [workspaces, allRooms]);

  const renderRoomList = (idPrefix: string, title: React.ReactNode, rooms: Room[]) => {
    if (rooms.length === 0) return null;

    const sortedRooms = [...rooms].sort((a, b) => a.name.localeCompare(b.name));

    const allSelected = sortedRooms.every(r => selectedRoomIds.has(r.id));

    return (
      <div key={idPrefix} className="mb-6 last:mb-0">
        <div className="flex items-center space-x-2 mb-2">
          <Checkbox
            id={`header-${idPrefix}`}
            checked={allSelected}
            onCheckedChange={(checked) => onToggleWorkspace(rooms, checked as boolean)}
          />
          <label htmlFor={`header-${idPrefix}`} className="text-sm font-semibold flex items-center gap-2 cursor-pointer select-none">
            {title}
            <Badge variant="outline" className="text-meta-sm h-5 px-1.5 font-normal text-muted-foreground">
              {rooms.length}
            </Badge>
          </label>
        </div>

        <div className="flex flex-wrap gap-2">
          {sortedRooms.map(room => (
            <div
              key={room.id}
              className={cn(
                "flex items-center space-x-2 px-3 py-1.5 rounded-full border cursor-pointer transition-colors select-none",
                selectedRoomIds.has(room.id) ? "bg-primary/10 border-primary/20" : "bg-background hover:bg-muted/50"
              )}
              onClick={() => onToggleRoom(room.id)}
            >
              <div className={cn(
                "w-4 h-4 rounded border flex items-center justify-center transition-colors px-0 py-0 min-w-4",
                selectedRoomIds.has(room.id) ? "bg-primary border-primary text-on-primary-text" : "border-border bg-background"
              )}>
                {selectedRoomIds.has(room.id) && <Check className="w-3 h-3" />}
              </div>
              <span className="text-sm truncate max-w-[120px]" title={room.name}>
                {room.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-background p-4 rounded-lg border shadow-sm space-y-2">
      {Array.from(workspaceRooms).map(([ws, rooms]) => (
        renderRoomList(
          ws.id,
          <><img className="w-4 h-4 mr-1 opacity-70" src="/assets/spc/groups-2.svg" alt="" /> {ws.name}</>,
          rooms
        )
      ))}
    </div>
  );
}

// Hit Component
/** 검색 결과 카드 형태 스켈레톤 */
const SearchSkeletonCard: React.FC<{ textWidths?: [string, string, string] }> = ({ textWidths = ['70%', '90%', '50%'] }) => (
  <div className="bg-background p-4 rounded-lg border mb-4">
    <div className="flex items-center gap-2 mb-2">
      <div className="h-5 w-16 skeleton-shimmer rounded-full" />
      <div className="h-3 w-24 skeleton-shimmer rounded-full" />
    </div>
    <div className="flex gap-3">
      <div className="w-10 h-10 rounded-full skeleton-shimmer shrink-0 mt-1" />
      <div className="flex-1 flex flex-col gap-2">
        <div className="h-4 skeleton-shimmer rounded-full" style={{ width: '100px' }} />
        <div className="h-3.5 skeleton-shimmer rounded-full" style={{ width: textWidths[0] }} />
        <div className="h-3.5 skeleton-shimmer rounded-full" style={{ width: textWidths[1] }} />
        <div className="h-3.5 skeleton-shimmer rounded-full" style={{ width: textWidths[2] }} />
      </div>
    </div>
  </div>
);

const SearchSkeletonGroup: React.FC = () => (
  <div className="space-y-4">
    <SearchSkeletonCard textWidths={['80%', '95%', '40%']} />
    <SearchSkeletonCard textWidths={['60%', '85%', '70%']} />
    <SearchSkeletonCard textWidths={['90%', '75%', '55%']} />
    <SearchSkeletonCard textWidths={['50%', '80%', '65%']} />
  </div>
);

function Hit({ hit }: { hit: any }) {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const { setMessageToShow } = useAppUIContext();
  const { toast } = useToast();

  useEffect(() => {
    if (contentRef.current && contentRef.current.clientHeight > 100) {
      setIsOverflowing(true);
    }
  }, [hit]);

  if (!hit.text && (!hit.files || hit.files.length === 0)) return null;

  const showMessage = async () => {
    try {
      const message = await Fire.msg.getMessage(hit.id);
      if (message) {
        setMessageToShow(message);
        return;
      }
    } catch {
      // Firestore Rules에 의해 직접 접근 실패 — 프록시 시도
    }

    // Cloud Function 프록시로 컨텍스트 조회 (canReadPastMessages 권한 필요)
    try {
      const context = await Fire.getMessageContext(hit.id, hit.roomId);
      if (context?.messages?.length > 0) {
        // 타겟 메시지를 찾아서 MessageModal로 전달
        const target = context.messages.find((m: any) => m.id === hit.id);
        if (target) {
          setMessageToShow(target);
          return;
        }
      }
    } catch {
      // 프록시도 실패 (권한 없음)
    }

    toast({ description: t`Message could not be found.` });
  };

  return (
    <div className="bg-background p-4 rounded-lg border shadow-sm hover:shadow-md transition-shadow mb-4">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2 overflow-hidden">
          <Badge variant="secondary" className="font-normal text-xs px-2 py-0.5" >
            #{My.Rooms.get(hit.roomId)?.name || hit.roomId}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {UtilApp.dateStrToTimeAndDay(hit.created, My.user.lang)}
          </span>
        </div>
      </div>

      <div className="flex gap-3">
        <Avatar className="w-10 h-10 mt-1">
          <AvatarImage src={hit['user.photoUrl'] || hit.photoUrl} />
          <AvatarFallback>?</AvatarFallback>
        </Avatar>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-semibold text-sm">
              <Highlight attribute="user.name" hit={hit} />
            </span>
          </div>

          <div className="relative group">
            {hit.text && (
              hit.text.includes('src=') ? (
                <div className="text-sm text-foreground prose prose-sm max-w-none whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: UtilApp.getTextAsHTML(hit.text, My.user.id) }} />
              ) : (
                <div className={cn("text-sm text-foreground break-words whitespace-pre-wrap relative", !expanded && "max-h-[100px] overflow-hidden")}>
                  <Highlight attribute="text" hit={hit} />
                  {!expanded && isOverflowing && (
                    <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent" />
                  )}
                </div>
              )
            )}

            {!expanded && isOverflowing && !hit.text?.includes('src=') && (
              <div className="flex justify-center mt-1">
                <button onClick={() => setExpanded(true)} className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground bg-muted/50 px-3 py-1 rounded-full border">
                  <ArrowDown className="w-3 h-3" /> <Trans>More</Trans>
                </button>
              </div>
            )}
          </div>

          {hit.files && hit.files.length > 0 && (
            <div className="mt-3 grid gap-2 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
              {hit.files.map((file: any, i: number) => (
                <MessageFileItem key={i} file={file} files={hit.files} showTitle={false} />
              ))}
            </div>
          )}
        </div>

        <Button
          className="w-8 h-8 mt-1 shrink-0"
          variant="icon-box"
          size="icon"
          onClick={showMessage}
          title={t`Go to message`}
        >
          <MessagesSquare />
        </Button>
      </div>
    </div>
  );
}

// Custom Results Component with proper Show More button
function CustomResults() {
  const { hits, isLastPage, showMore } = useInfiniteHits();
  const { status } = useInstantSearch();

  // 검색 중(loading/stalled)이면 스피너 표시 → "결과 없음" 깜빡임 방지
  if (status === 'loading' || status === 'stalled') {
    return <SearchSkeletonGroup />;
  }

  if (hits.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <p><Trans>No results found.</Trans></p>
      </div>
    );
  }

  return (
    <div className="space-y-4 pb-10">
      {hits.map((hit: any) => (
        <Hit key={hit.objectID} hit={hit} />
      ))}

      {!isLastPage && (
        <div className="flex justify-center pt-4">
          <Button variant="outline" onClick={() => showMore()}>
            <Trans>Show more results</Trans>
          </Button>
        </div>
      )}
    </div>
  );
}

// -- Main Component --

const SearchPage: React.FC = () => {
  const [searchClient, setSearchClient] = useState<SearchClient>();
  const [searchQuery, setSearchQuery] = useState('');
  const [inputValue, setInputValue] = useState('');
  const { roomId } = useParams<{ roomId: string }>();
  const history = useHistory();
  const { workspaces, setIsMobileMenuOpen, activeWorkspaceId } = useAppUIContext();
  const [allRooms, setAllRooms] = useState<Room[]>([]);
  const { toast } = useToast();
  const [myNotifCount, setMyNotifCount] = useState(0);

  useEffect(() => {
    setMyNotifCount(MyNotifs.NrOfNew);
    // Poll for notifications or subscribe if possible
    const interval = setInterval(() => {
      setMyNotifCount(MyNotifs.NrOfNew);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  // Selected Rooms State
  const [selectedRoomIds, setSelectedRoomIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    Fire.getSearchSecuredAPIKey().then((key) => {
      setSearchClient(algoliasearch(import.meta.env.VITE_ALGOLIA_APP_ID, key));
    });
  }, []);

  // Subscribe to room updates ensure data is available even if LeftMenuRooms is not loaded
  useEffect(() => {
    const unsub = Fire.subscribeToMyRooms(My.user.id, (roomList) => {
      My.setRooms(roomList);
      setAllRooms(roomList);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    const rooms = Array.from(My.Rooms.values());
    setAllRooms(rooms);

    if (roomId && My.Rooms.has(roomId)) {
      setSelectedRoomIds(new Set([roomId]));
    }
  }, [roomId, workspaces]);

  const toggleRoom = (id: string) => {
    const newSet = new Set(selectedRoomIds);
    if (newSet.has(id)) newSet.delete(id);
    else newSet.add(id);
    setSelectedRoomIds(newSet);
  };

  const toggleWorkspace = (rooms: Room[], select: boolean) => {
    const newSet = new Set(selectedRoomIds);
    rooms.forEach(r => {
      if (select) newSet.add(r.id);
      else newSet.delete(r.id);
    });
    setSelectedRoomIds(newSet);
  };

  const handleSearch = () => {
    if (selectedRoomIds.size === 0) {
      toast({
        description: t`Please select at least one channel to search.`
      });
      return;
    }
    if (!inputValue.trim()) return;
    setSearchQuery(inputValue.trim());
  };

  // Generate Facet Filters
  const facetFilters = React.useMemo(() => {
    if (selectedRoomIds.size === 0) return [];
    return [Array.from(selectedRoomIds).map(id => `roomId:${id}`)];
  }, [selectedRoomIds]);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background md:rounded-panel md:border md:border-border">
      <header className="bg-background border-b px-2 md:px-6 py-2 md:py-4 flex items-center justify-between shrink-0 z-10">
        <div className="flex items-center gap-2 md:gap-4">

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden relative"
            onClick={() => setIsMobileMenuOpen(true)}
            data-cy='menu'
          >
            <MenuIcon className="w-6 h-6" />
            {myNotifCount > 0 && (
              <Badge className="absolute -top-1 -right-1 h-4 w-4 p-0 flex items-center justify-center bg-destructive hover:bg-destructive/90 text-meta-sm">
                {myNotifCount > 9 ? '9+' : myNotifCount}
              </Badge>
            )}
          </Button>
          <h1 className="text-lg md:text-xl font-bold"><Trans>Message Search</Trans></h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto w-full max-w-7xl mx-auto p-4 md:p-6 space-y-6">
        {/* Search Input - Outside InstantSearch */}
        <div className="flex gap-2 w-full">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSearch();
              }}
              placeholder={t`Search for messages and files`}
              className="pl-9"
              autoFocus
            />
            {inputValue && (
              <button
                onClick={() => { setInputValue(''); setSearchQuery(''); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <Button onClick={handleSearch} disabled={!inputValue.trim()}>
            <Trans>Search</Trans>
          </Button>
        </div>

        {/* Room Selector */}
        <RoomSelector
          workspaces={workspaces}
          allRooms={allRooms.filter(r => r.workspaceId === activeWorkspaceId || (!r.workspaceId && !activeWorkspaceId))}
          selectedRoomIds={selectedRoomIds}
          onToggleRoom={toggleRoom}
          onToggleWorkspace={toggleWorkspace}
        />

        <Separator className="my-6" />

        {/* Results */}
        {!searchQuery ? (
          <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
            <Search className="w-16 h-16 mb-4 opacity-20" />
            <p className="text-lg font-medium"><Trans>Select channels and enter a keyword to search.</Trans></p>
          </div>
        ) : searchClient ? (
          <InstantSearch
            key={`search-${searchQuery}-${Array.from(selectedRoomIds).join(',')}`}
            indexName="messages"
            searchClient={searchClient}
          >
            <SearchQuerySync query={searchQuery} />
            {/* @ts-ignore */}
            <Configure hitsPerPage={20} facetFilters={facetFilters} />

            <CustomResults />
          </InstantSearch>
        ) : (
          <div className="px-4 pt-4">
            <SearchSkeletonGroup />
          </div>
        )}
      </main>
    </div>
  );
};

export default SearchPage;